<template>
  <div class="content">
    <div ref="tableWrapper" class="matrix-wrapper">
      <div v-show="isTableRendering" class="render-block"></div>
      <span class="axis label-top">{{ $t('dialogs.destinations') }}</span>
      <span class="axis label-left">{{ $t('dialogs.origins') }}</span>
      <prime-data-table :value="matrixCsv" scrollable scroll-height="40vh">
        <prime-column
          v-for="col of colKeys"
          :key="col"
          :field="col"
          :header="col === 'source_id' ? 'Gen. ID' : col"
          :frozen="col === 'source_id'"
        >
          <template #body="{ data }">
            <span :style="{ 'font-weight': col === 'source_id' ? 'bold' : 'normal' }">
              {{ Math.round(data[col] * 1000) / 1000 }}
            </span>
          </template>
        </prime-column>
      </prime-data-table>
    </div>
  </div>

  <div class="p-d-flex p-jc-center p-mb-3">
    <tm-button
      type="create"
      icon="ri-download-2-line"
      :tooltip="$t('dialogs.export matrix')"
      @click="downloadEntireMatrixAsCsv()"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import useDialog from '@composables/useDialog';
import PrimeDataTable from 'primevue/datatable';
import PrimeColumn from 'primevue/column';
import useExport, { CsvCell } from '@composables/useExport';
import { debounce } from 'lodash-es';

const { dialog } = useDialog();
const { downloadAsCsv } = useExport();

const scrollStepItems = 20;
const tableWrapper = ref<HTMLElement>();
const colsDisplayed = ref(scrollStepItems);
const rowsDisplayed = ref(scrollStepItems);
const isTableRendering = ref(false);

const colKeys = computed(() => {
  const cols = getOrderedColKeys();
  return cols.slice(0, colsDisplayed.value);
});

const matrixCsv = computed<CsvCell[]>(() => {
  const csvData = dialog.value.data?.matrixCsv;
  if (!csvData) return [];
  return csvData.slice(0, rowsDisplayed.value);
});

const addColumns = async () => {
  const maxCols = dialog.value.data?.matrixCsv?.length || 0;
  if (colsDisplayed.value >= maxCols) return;
  await startLoading();
  colsDisplayed.value = Math.min(colsDisplayed.value + scrollStepItems, maxCols);
};

const addRows = async () => {
  const maxRows = dialog.value.data?.matrixCsv?.length || 0;
  if (rowsDisplayed.value >= maxRows) return;
  await startLoading();
  rowsDisplayed.value = Math.min(rowsDisplayed.value + scrollStepItems, maxRows);
};

const handleScroll = debounce((ev: Event) => {
  const scrollable = ev.target as HTMLElement;
  const { scrollTop, clientHeight, scrollHeight, scrollWidth, clientWidth, scrollLeft } = scrollable;
  const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 100;
  const isAtRight = Math.abs(scrollWidth - clientWidth - scrollLeft) < 50;
  if (isAtBottom) addRows();
  if (isAtRight) addColumns();
}, 100);

const getOrderedColKeys = () => {
  const csvDataRow = dialog.value.data?.matrixCsv?.[0];
  if (!csvDataRow) return [];
  const colKeys = Object.keys(csvDataRow);
  const frozenCol = colKeys.pop(); // 'source_id' col is ordered last, since all other keys are numeric
  if (frozenCol) colKeys.unshift(frozenCol); // move 'source_id' col to the first position
  return colKeys;
};

const addScrollListener = () => {
  if (!tableWrapper.value) return;
  const scrollable = tableWrapper.value.querySelector('.p-datatable-wrapper');
  if (!scrollable) return;
  scrollable.addEventListener('scroll', handleScroll);
};

const addTableRenderObserver = () => {
  if (!tableWrapper.value) return;
  const tableBody = tableWrapper.value.querySelector('tbody');
  if (!tableBody) return;
  const finishLoading = debounce(() => (isTableRendering.value = false), 100);
  const observer = new MutationObserver(() => finishLoading());
  observer.observe(tableBody, { childList: true, subtree: true });
};

const downloadEntireMatrixAsCsv = () => {
  const fileName = 'matrix';
  downloadAsCsv(dialog.value.data?.matrixCsv || [], fileName, getOrderedColKeys());
};

const startLoading = () => {
  isTableRendering.value = true;
  return new Promise<void>((resolve) => setTimeout(resolve, 100)); // wait so the loading state has time to propagate (before adding new content which can lag the DOM)
};

onMounted(() => {
  addScrollListener();
  addTableRenderObserver();
});
</script>

<style scoped>
.matrix-wrapper {
  position: relative;
  padding: 2rem;
  min-width: 60vw;
}
.p-datatable {
  border: solid 1px #ced4da;
}
.p-datatable :deep(.p-datatable-thead > tr > th:not(:first-of-type)) {
  border-bottom: 2px solid red;
}
.p-datatable :deep(.p-datatable-tbody tr > td:first-of-type) {
  border-right: 2px solid green;
  background-color: #f8f9fa;
}
.p-datatable :deep(.p-datatable-tbody > tr > td),
.p-datatable :deep(.p-datatable-thead > tr > th) {
  padding: 1rem;
  min-width: 6rem;
  overflow: hidden;
  justify-content: center;
}
.axis {
  display: block;
  position: absolute;
}
.axis.label-top {
  top: 0;
  left: 50%;
  color: red;
}
.axis.label-left {
  top: 50%;
  left: -1rem;
  transform: rotate(270deg);
  color: green;
}
.render-block {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  z-index: 5;
  background-color: rgba(0, 0, 0, 0.25);
}
</style>
